import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'ui/Modal';
import api from 'config/axiosConfig';
import { Settings, X, Check, Sparkles } from 'lucide-react';
import ActorAvatar from 'components/Actors/ActorAvatar';
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes';
import type { Actor } from 'types/actor';
import dayjs, { formatNotificationTimestamp } from 'config/dayjsConfig';
import { getEntityDetails, getActorDetails } from 'util/getObjectDetails';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import {
  isPushNotificationSupported,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications
} from 'util/pushNotifications';
import { toast } from 'sonner';
import { useUser } from 'util/UserContext';
import EmptyState from 'ui/EmptyState';
import { apiRoutes } from "util/apiRoutes";
import { getNotificationUiStrings, resolveNotificationLocale } from "i18n/notificationUi";
interface NotificationProps {
    id: number | string;
    user_id?: number;
    actor_id: number;
    actor_name: string;
    actor_type: 'user' | 'team';
    actor_display_name: string;
    actor_team_name: string;
    requestor_type?: 'user' | 'team';
    requestor_id?: number;
    requestor_name?: string;
    requestor_properties_public?: any;
    requestee_type?: 'user' | 'team';
    requestee_id?: number;
    requestee_name?: string;
    requestee_properties_public?: any;
    related_entity_type?: string;
    related_entity_id?: number;
    actor_properties_public: {
        avatar_key?: string;
        avatar_url?: string;
        is_private?: boolean;
    };
    entity_name: string;
    entity_properties_public: {
        avatar_key?: string;
        avatar_url?: string;
        is_private?: boolean;
    };
    notification_type: 'follow_new' | 'follow_request' | 'follow_accept' | 'coauthor_invite' | 'team_join_request' | 'team_invite' | 'vote_up' | 'comment_post' | 'comment_reply' | 'comment_mention' | 'teabate_argument_mention' | 'teabate_argument_reply' | 'teabate_argument_post' | 'crosspost_new' | 'crosspost_mention' | 'crosspost_profile' | 'new_content' | 'new_action' | 'new_teabate' | 'subscription_comment' | 'subscription_impact' | 'subscription_teabate' | 'subscription_solution' | 'subscription_action' | 'subscription_influencer' | 'subscription_influenced' | 'chat_new_message' | 'chat_request' | 'chat_mention' | 'chat_reaction' | 'chat_thread_reply' | 'chat_poll_vote' | 'content_award_received' | 'quiz_player_joined' | 'quiz_player_completed' | 'quiz_all_completed';
    entity_type?: string;
    entity_id?: number;
    is_read: boolean;
    request_id?: number;
    request_status?: string;
    properties: {
        isCoauthor?: boolean;
        commentPreview?: string;
        entityTitle?: string;
        impact_type?: string;
        mentioningUsername?: string;
        argumentType?: string;
        quiz_code?: string;
        quiz_phase?: "part1" | "part2";
        [key: string | number]: any;
    };
    created_at: string;
    updated_at?: string;
}

interface Props {
    onClose: () => void;
    onNotificationsRead: (count: number) => void;
}

/**
 * Ensure notification has a safe actor display name.
 * If the actor is anonymous (actor_id === 0) or the provided actor name is falsy,
 * set a stable placeholder display name to avoid runtime errors and confusing UI.
 */
const sanitizeNotification = (notification: NotificationProps): NotificationProps => {
    // Derive a safe actor display name. This value is used throughout the UI and must always be a non-empty string.
    const isAnonymous: boolean = notification.actor_id === 0;
    const providedName: string | undefined = (notification.actor_name as unknown as string) || notification.actor_team_name || notification.actor_display_name;
    const safeActorName: string = isAnonymous ? "[anonymous]" : (providedName && providedName.trim().length > 0 ? providedName : "[anonymous]");

    // Return a new object with a guaranteed actor_name. Leave other properties unchanged.
    return {
        ...notification,
        actor_name: safeActorName
    };
};

const NotificationsModal: React.FC<Props> = ({ onClose, onNotificationsRead }) => {
    const { currentUser } = useUser();
    const notificationUi = getNotificationUiStrings(resolveNotificationLocale());
    const [notifications, setNotifications] = useState<NotificationProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();

    /**
     * Build an `Actor` from a notification actor/entity row. Notification
     * payloads carry many actor-like shapes (actor_*, entity_*,
     * requestor_*, requestee_*); this helper normalizes any of them so
     * we can hand the result to the standardized `<ActorAvatar>` and
     * get the same Instagram-style behavior (story ring, profile link)
     * as the rest of the product.
     */
    const buildNotificationActor = (params: {
        id: number | undefined;
        name: string | undefined;
        type: 'user' | 'team' | undefined;
        properties_public: { avatar_key?: string; avatar_url?: string; is_private?: boolean } | undefined;
    }): Actor | null => {
        if (!params.id || params.id <= 0) {
            return null;
        }
        const username = (params.name || '').trim();
        if (!username) {
            return null;
        }
        const avatarKey = (params.properties_public?.avatar_key || '').trim();
        return {
            id: params.id,
            kind: params.type === 'team' ? 'team' : 'user',
            username,
            displayName: null,
            avatarKey: avatarKey.length > 0 ? avatarKey : null,
        };
    };

    /**
     * Render a notification row avatar via the standardized
     * `<ActorAvatar>`. Falls back to a neutral placeholder circle when
     * the actor cannot be determined (very rare edge case for malformed
     * notification payloads).
     */
    const renderNotificationAvatar = (actor: Actor | null, fallbackInitial: string): React.ReactNode => {
        if (!actor) {
            return (
                <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-eoe-bg-hover text-sm font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text">
                    {fallbackInitial}
                </div>
            );
        }
        return (
            <ActorAvatar
                actor={actor}
                size={ACTOR_AVATAR_SIZES.inboxRow}
            />
        );
    };

    // Add state to track loaded entity details
    const [entityDetails, setEntityDetails] = useState<Record<string, any>>({});
    const [notificationEntityDetails, setNotificationEntityDetails] = useState<Record<string, any>>({});

    // Add state for push notifications
    const [pushSupported, setPushSupported] = useState(false);
    // Track permission lazily via Notification.permission when needed
    const [pushRegistering, setPushRegistering] = useState(false);
    const [hasPushSubscription, setHasPushSubscription] = useState(false);

    // Check if push notifications are supported on initial load
    useEffect(() => {
        const supported = isPushNotificationSupported();
        setPushSupported(supported);
        
        // Permission can be checked on demand via Notification.permission
        // Check if a push subscription already exists
        const checkExistingSubscription = async () => {
            try {
                if (!supported || !('serviceWorker' in navigator)) return;
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                setHasPushSubscription(Boolean(subscription));
            } catch (err) {
                // Non-fatal
                setHasPushSubscription(false);
            }
        };
        checkExistingSubscription();
    }, []);

    const fetchNotifications = useCallback(async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await api.get<{
                notifications: NotificationProps[];
                nextCursor: string | null;
                nextCursorId: number | null;
                hasMore: boolean;
            }>(apiRoutes.notifications.root, {
                params: {
                    limit: 20,
                    offset: (pageNum - 1) * 20
                }
            });

            // API now returns `{ notifications, nextCursor, nextCursorId, hasMore }`.
            // Tolerate older deployments that may still return a bare array.
            const rawList: NotificationProps[] = Array.isArray(response.data)
                ? (response.data as NotificationProps[])
                : (response.data?.notifications ?? []);
            const hasMoreFlag: boolean = Array.isArray(response.data)
                ? rawList.length >= 20
                : Boolean(response.data?.hasMore);

            setHasMore(hasMoreFlag);

            const sanitizedBatch: NotificationProps[] = rawList.map((n) => sanitizeNotification(n));

            setNotifications(prevNotifications => {
                const newNotifications = sanitizedBatch.filter(
                    (newNotif: NotificationProps) => !prevNotifications.some(notif => notif.id === newNotif.id)
                );
                return [...prevNotifications, ...newNotifications];
            });
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setNotifications([]);
        setPage(1);
        setHasMore(true);
        fetchNotifications(1);
    }, [fetchNotifications]);

    useEffect(() => {
        if (page > 1) {
            fetchNotifications(page);
        }
    }, [page, fetchNotifications]);

    const observer = useRef<IntersectionObserver | null>(null);
    const lastNotificationElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const handleMarkAllAsRead = async () => {
        try {
            await api.patch(apiRoutes.notifications.markAllRead);
            let markedCount = 0;
            setNotifications(prevNotifications => {
                markedCount = prevNotifications.filter(n => !n.is_read).length;
                return prevNotifications.map(notif => ({ ...notif, is_read: true }));
            });
            onNotificationsRead(markedCount);
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    const markNotificationAsRead = async (notificationId: number) => {
        try {
            await api.patch(apiRoutes.notifications.markRead, {
                notification_ids: [notificationId]
            });
            setNotifications(prevNotifications =>
                prevNotifications.map(notif =>
                    notif.id === notificationId ? { ...notif, is_read: true } : notif
                )
            );
            onNotificationsRead(1);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const loadEntityDetails = useCallback(async (notification: NotificationProps) => {
        const { entity_type, entity_id, request_status, related_entity_type, related_entity_id, notification_type } = notification;
        
        // For comment mentions and replies, we may need both the parent entity and the comment entity
        if (notification_type === 'comment_mention' || notification_type === 'comment_reply') {
            // Load the comment entity if available
            if (entity_type === 'comment' && entity_id) {
                const commentCacheKey = `comment-${entity_id}`;
                if (!notificationEntityDetails[commentCacheKey]) {
                    try {
                        const commentDetails = await getEntityDetails('comment', entity_id);
                        setNotificationEntityDetails(prev => ({
                            ...prev,
                            [commentCacheKey]: commentDetails
                        }));
                    } catch (error) {
                        console.error('Error loading comment details:', error);
                    }
                }
            }
            
            // Load the related entity (the parent entity where the comment was posted)
            if (related_entity_type && related_entity_id) {
                const relatedCacheKey = `${related_entity_type}-${related_entity_id}`;
                if (!entityDetails[relatedCacheKey]) {
                    try {
                        const details = related_entity_type === 'team' || related_entity_type === 'user'
                            ? await getActorDetails(related_entity_type, related_entity_id)
                            : await getEntityDetails(related_entity_type, related_entity_id);
                            
                        setEntityDetails(prev => ({
                            ...prev,
                            [relatedCacheKey]: details
                        }));
                    } catch (error) {
                        console.error('Error loading related entity details:', error);
                    }
                }
            }
        }
        
        // For upvotes, comments and other standard notifications
        if ((notification_type === 'vote_up' || notification_type === 'comment_post') && entity_type && entity_id) {
            const entityCacheKey = `${entity_type}-${entity_id}`;
            if (!entityDetails[entityCacheKey]) {
                try {
                    const details = entity_type === 'team' || entity_type === 'user'
                        ? await getActorDetails(entity_type, entity_id)
                        : await getEntityDetails(entity_type, entity_id);
                        
                    setEntityDetails(prev => ({
                        ...prev,
                        [entityCacheKey]: details
                    }));
                } catch (error) {
                    console.error('Error loading entity details:', error);
                }
            }
        }

        // Original logic for other notification types
        if (!related_entity_type || !related_entity_id || !entity_type || !entity_id || !request_status) return;

        const cacheKey = `${related_entity_type}-${related_entity_id}`;
        if (entityDetails[cacheKey]) return;

        const notificationCacheKey = `${entity_type}-${entity_id}`;
        if (notificationEntityDetails[notificationCacheKey]) return;

        try {
            const details = related_entity_type === 'team' || related_entity_type === 'user'
                ? await getActorDetails(related_entity_type, related_entity_id)
                : await getEntityDetails(related_entity_type, related_entity_id);
            const notificationEntity = entity_type === 'team' || entity_type === 'user'
                ? await getActorDetails(entity_type, entity_id)
                : await getEntityDetails(entity_type, entity_id);

            setEntityDetails(prev => ({
                ...prev,
                [cacheKey]: details
            }));
            setNotificationEntityDetails(prev => ({
                ...prev,
                [notificationCacheKey]: notificationEntity
            }));
        } catch (error) {
            console.error('Error loading entity details:', error);
        }
    }, [entityDetails, notificationEntityDetails]);

    const getNotificationAvatar = (notification: NotificationProps) => {
        const { entity_type, request_status, updated_at, notification_type } = notification;
        
        // Get cached entity details
        const cacheKey = notification.related_entity_type && notification.related_entity_id
            ? `${notification.related_entity_type}-${notification.related_entity_id}`
            : null;
        const entityDetail = cacheKey ? entityDetails[cacheKey] : null;

        if (notification_type === 'content_award_received') {
            return (
                <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    <Sparkles className="size-5" aria-hidden />
                </div>
            );
        }

        if (notification.actor_id === 0) {
            // Anonymous notifications are intentionally non-clickable
            // (no actor profile to link to) and use the brand mascot
            // image instead of an actor avatar.
            return (
                <span className="flex size-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-eoe-dark-bg-hover dark:bg-eoe-gray">
                    <img className="h-full w-full object-cover" src="/avatea_incognito_nobg.png" alt="Anonymous user" />
                </span>
            );
        }

        // For nearly every notification kind the row's avatar represents
        // the actor (the user/partea who triggered the notification). The
        // standardized `<ActorAvatar>` paints the story ring, links to
        // the story when present, and falls back to the profile.
        const actorActor = buildNotificationActor({
            id: notification.actor_id,
            name: notification.actor_name,
            type: notification.actor_type,
            properties_public: notification.actor_properties_public,
        });

        if (notification_type === 'vote_up' ||
            notification_type === 'comment_post' ||
            notification_type === 'comment_reply' ||
            notification_type === 'comment_mention' ||
            notification_type === 'teabate_argument_post' ||
            notification_type === 'teabate_argument_reply' ||
            notification_type === 'teabate_argument_mention' ||
            notification_type.startsWith('chat_')) {
            return renderNotificationAvatar(
                actorActor,
                notification.actor_name.charAt(0).toUpperCase()
            );
        }

        if (notification_type === 'follow_new') {
            return renderNotificationAvatar(
                actorActor,
                notification.actor_name.charAt(0).toUpperCase()
            );
        }
        
        // Original request-based notification handling
        const entityActor = buildNotificationActor({
            id: notification.entity_id,
            name: notification.entity_name,
            type: notification.entity_type === 'team' ? 'team' : 'user',
            properties_public: notification.entity_properties_public,
        });
        const requestorActor = buildNotificationActor({
            id: notification.requestor_id,
            name: notification.requestor_name,
            type: notification.requestor_type,
            properties_public: notification.requestor_properties_public,
        });
        const requesteeActor = buildNotificationActor({
            id: notification.requestee_id,
            name: notification.requestee_name,
            type: notification.requestee_type,
            properties_public: notification.requestee_properties_public,
        });

        switch (request_status) {
            case 'accepted':
                if (!updated_at) {
                    if (!notification.updated_at || !notification.properties?.request_status) {
                        return renderNotificationAvatar(
                            entityActor,
                            notification.entity_name.charAt(0).toUpperCase()
                        );
                    }
                    if (notification.requestee_type === 'user') {
                        return renderNotificationAvatar(
                            entityActor,
                            notification.entity_name.charAt(0).toUpperCase()
                        );
                    }
                    return renderNotificationAvatar(
                        entityActor,
                        notification.entity_name.charAt(0).toUpperCase()
                    );
                }
                if (notification.requestee_id) {
                    if (notification.requestee_type === 'user') {
                        return renderNotificationAvatar(
                            requestorActor,
                            (notification.requestor_name || '?').charAt(0).toUpperCase()
                        );
                    }
                    return renderNotificationAvatar(
                        requesteeActor,
                        (notification.requestee_name || '?').charAt(0).toUpperCase()
                    );
                }
                return renderNotificationAvatar(
                    entityActor,
                    notification.entity_name.charAt(0).toUpperCase()
                );
                
            case 'pending':
                if (entity_type === 'team' || notification.related_entity_type === 'team') {
                    if (notification.related_entity_type !== 'team') {
                        return renderNotificationAvatar(
                            actorActor,
                            notification.actor_name.charAt(0).toUpperCase()
                        );
                    }
                    const entityDetailActor = buildNotificationActor({
                        id: typeof entityDetail?.entity_id === 'number' ? entityDetail.entity_id : undefined,
                        name: entityDetail?.entity_name,
                        type: 'team',
                        properties_public: entityDetail?.properties_public,
                    });
                    return renderNotificationAvatar(
                        entityDetailActor,
                        (entityDetail?.entity_name || '?').charAt(0).toUpperCase()
                    );
                }
                if (!notification.updated_at) {
                    return renderNotificationAvatar(
                        actorActor,
                        notification.actor_name.charAt(0).toUpperCase()
                    );
                }
                return renderNotificationAvatar(
                    entityActor,
                    notification.entity_name.charAt(0).toUpperCase()
                );

            default:
                // Default: render the actor's avatar with the standard
                // story-or-profile click behavior.
                return renderNotificationAvatar(
                    actorActor,
                    notification.actor_name.charAt(0).toUpperCase()
                );
        }
    };

    /** Resolve display/route entity type: teabates are stored as 'impact' but route as 'teabate'. */
    const resolveEntityDisplayType = (entityType: string | undefined, isTeabate?: boolean): string => {
        if (entityType === "impact" && isTeabate) return "teabate";
        return entityType || "";
    };

    const getNotificationMessage = (notification: NotificationProps) => {
        let { notification_type, entity_type, entity_id, request_status, related_entity_type, related_entity_id, properties } = notification;
        // Get cached entity details
        const cacheKey = related_entity_type && related_entity_id
            ? `${related_entity_type}-${related_entity_id}`
            : null;
        const notificationCacheKey = entity_type && entity_id 
            ?`${entity_type}-${entity_id}`
            : null;
        const entityDetail = cacheKey ? entityDetails[cacheKey] : null;
        const notificationEntity = notificationCacheKey ? notificationEntityDetails[notificationCacheKey] : null;
        const isCoauthor = properties?.isCoauthor === true;
        
        switch (notification_type) {
            case 'follow_new':
                return (related_entity_type === 'team' 
                    ? 
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} started following partea {' '}
                            <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                {notificationEntity?.entity_name}
                            </a>
                        </span>
                    </span>
                    : 
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {` `} started following you
                        </span>
                    </span>
                );
            
            case 'vote_up':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} prioritized {isCoauthor ? ' ' : ' '}
                            <a href={`/${resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}/${notification.entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}{': '}
                                {properties?.entityTitle ? 
                                    <span className={`${notification.entity_type === 'comment' ? 'font-medium' : 'font-bold'} text-eoe-text dark:text-eoe-dark-text`}>{properties.entityTitle}</span> : 
                                    ''}
                            </a>
                            {isCoauthor ? ' that you collaborated on' : ''}
                        </span>
                    </span>
                );

            case 'comment_post':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} commented on {properties?.entityTitle ? ' ' : isCoauthor ? notification.entity_type === 'issue' || notification.entity_type === 'action' || (notification.entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a ': ' '}
                            <a href={`/${resolveEntityDisplayType(notification.related_entity_type, properties?.is_teabate)}/${notification.related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(notification.related_entity_type, properties?.is_teabate)}{' '}
                                {properties?.entityTitle ? 
                                    <span className="font-bold text-eoe-text dark:text-eoe-dark-text">{properties.entityTitle}{': '}</span> : 
                                    ''}
                            </a>
                            {isCoauthor && ' that you collaborated on'}
                        </span>
                        {properties?.commentPreview && (
                            <a href={`/${notification.entity_type}/${notification.entity_id}`} className='text-eoe-text dark:text-eoe-dark-text max-w-full overflow-hidden text-ellipsis'>
                               {' '}{properties.commentPreview}
                            </a>
                        )}
                    </span>
                );

            case 'comment_reply':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} replied to {isCoauthor ? 'a comment you collaborated on on ' : 'your comment on '}
                            <a href={`/${resolveEntityDisplayType(notification.related_entity_type, properties?.is_teabate)}/${notification.related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(notification.related_entity_type, properties?.is_teabate)}{' '}
                                {properties?.entityTitle ? 
                                    <span className="font-bold text-eoe-text dark:text-eoe-dark-text">{properties.entityTitle}{': '}</span> : 
                                    ''}
                            </a>
                        </span>
                            {(properties?.commentPreview || properties?.entityTitle) && (
                                <a href={`/${notification.entity_type}/${notification.entity_id}`} className='text-eoe-text dark:text-eoe-dark-text max-w-full overflow-hidden text-ellipsis'>
                                    {' '}{properties.commentPreview || properties.entityTitle}
                                </a>
                            )}
                    </span>
                );

            case 'comment_mention':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} mentioned you on {entity_type === 'issue' || (notification.entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            {notification.entity_type && notification.entity_id && (
                                <a href={`/${resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}/${notification.entity_id}`} className='font-medium'>
                                    {resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}{':'}
                                </a>
                            )}
                            {(properties?.commentPreview || properties?.entityTitle) && (
                                <a href={`/${resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}/${notification.entity_id}`} className='text-eoe-text dark:text-eoe-dark-text max-w-full overflow-hidden text-ellipsis'>
                                    {' '}{properties.commentPreview || properties.entityTitle}
                                </a>
                            )}
                        </span>
                    </span>
                );

            case 'chat_new_message':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} sent you a message
                        </span>
                        {properties?.messagePreview && (
                            <span className="text-eoe-text dark:text-eoe-dark-text"> {properties.messagePreview}</span>
                        )}
                    </span>
                );

            case 'chat_request':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} sent you a chat request
                        </span>
                        {properties?.messagePreview && (
                            <span className="text-eoe-text dark:text-eoe-dark-text"> {properties.messagePreview}</span>
                        )}
                    </span>
                );

            case 'chat_mention':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} mentioned you in chat
                        </span>
                        {properties?.messagePreview && (
                            <span className="text-eoe-text dark:text-eoe-dark-text"> {properties.messagePreview}</span>
                        )}
                    </span>
                );

            case 'chat_reaction':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} reacted to your message
                        </span>
                        {properties?.reactionKey && (
                            <span className="text-eoe-text dark:text-eoe-dark-text"> {properties.reactionKey}</span>
                        )}
                    </span>
                );

            case 'chat_thread_reply':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} replied in a chat thread
                        </span>
                        {properties?.messagePreview && (
                            <span className="text-eoe-text dark:text-eoe-dark-text"> {properties.messagePreview}</span>
                        )}
                    </span>
                );

            case 'chat_poll_vote':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} voted on your chat poll
                        </span>
                    </span>
                );
            
            case 'follow_request':
                if (request_status === 'pending') {
                    return (related_entity_type === 'team' ? (
                            <>
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                    {notification.actor_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} wants to follow partea {' '}
                                    <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {notificationEntity?.entity_name}
                                    </a>
                                </span>
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                    {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                   {` `} wants to follow you
                                </span>
                            </span>
                        </>
                    ));
                } else if (request_status === 'accepted') {
                    return (related_entity_type === 'team' ? (
                        <>
                        {notification.updated_at ? (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`} className='font-semibold'>
                                    {notification.entity_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {` `} started following partea {' '}
                                    <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {entityDetail?.entity_name}
                                    </a>
                                    {` `} {`(accepted by`} {' '}
                                    <a href={`/user/${notification.actor_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {notification.actor_name}{`)`}
                                    </a>
                                </span>
                            </span>
                        ) : (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        you started following partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                        {` `} {`(accepted by`} {' '}
                                        <a href={`/user/${notification.actor_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notification.actor_name}{`)`}
                                        </a>
                                    </span>
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                        {notification.actor_id === notification.user_id ? (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.requestor_type === 'user' ? 'user' : 'partea'}/${notification.requestor_name}`} className='font-semibold'>
                                    {notification.requestor_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} started following you
                                </span>
                            </span>
                        ) : (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    You started following {' '}
                                </span>
                                <a href={`/${notification.requestee_type === 'user' ? 'user' : 'partea'}/${notification.requestee_name}`} className='font-semibold'>
                                    {notification.requestee_name}
                                </a>
                            </span>
                        )}
                        </>
                    ));
                } else {
                    return null;
                }
            case 'coauthor_invite':
                switch (request_status) {
                    case 'pending':
                        return (entity_type === 'team' ? (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} wants partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                        {' '} to collaborate on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type}{': '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} wants you to collaborate on {' '}
                                    <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                        {related_entity_type}{': '} {entityDetail?.entity_name || related_entity_type}
                                    </a>
                                    </span>
                                </span>
                            </>
                        ));
                    case 'accepted':
                        return (entity_type === 'team' ? (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    Partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} is now collaborating with you on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type}{': '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            </>
                        ) : (
                            <>{notification.updated_at ? (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    {notification.requestee_type === 'team' ?<> Partea {` `}
                                    <a href={`/partea/${notification.requestee_name}`} className='font-semibold'>
                                        {notification.requestee_name}
                                    </a> {` `} is
                                    </> : 
                                    <>
                                    You are
                                    </>
                                    }
                                    {``} now collaborating with {' '}
                                    <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`} className='font-semibold'>
                                        {notification.entity_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} on {' '}
                                    <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                        {related_entity_type}{': '} {entityDetail?.entity_name || related_entity_type}
                                    </a>
                                    </span>
                                </span>
                            ) : (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a> 
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} is now collaborating with you on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type}{': '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            )}
                            </>
                        ));
                
                    default:
                        return 'requested to collaborate on'
                }
            case 'team_join_request':
                switch (request_status) {
                    case 'pending':
                        return (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} wants to join partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                    </span>
                                </span>
                            </>
                        );
                    case 'accepted':
                        return (
                                <>
                                {notification.updated_at ? (
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <a href={`/${notification.requestor_type === 'user' ? 'user' : 'partea'}/${notification.requestor_name}`} className='font-semibold'>
                                            {notification.requestor_name}
                                        </a>
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            {' '} joined partea {' '}
                                            <a href={`/partea/${entityDetail?.entity_name}`} className='font-semibold text-eoe-text dark:text-eoe-dark-text'>
                                                {entityDetail?.entity_name || related_entity_type}
                                            </a>
                                            {` `} {`(accepted by `}
                                            <a href={`/user/${notification.actor_name}`} className='font-semibold text-eoe-text dark:text-eoe-dark-text'>
                                                {notification.actor_name}
                                            </a>
                                            {`)`}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            You joined partea {' '}
                                            <a href={`/partea/${notification.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                                {notification.entity_name}
                                            </a>
                                        </span>
                                    </span>
                                )}
                                </>
                            );
                
                    default:
                        return 'requested to collaborate on'
                }
            case 'team_invite':
                switch (request_status) {
                    case 'pending':
                        return (
                                <>
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                        </a>
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            {' '} invited you to partea {' '}
                                            <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                                {entityDetail?.entity_name}
                                            </a>
                                        </span>
                                    </span>
                                </>
                            );
                    case 'accepted':
                        return (
                            <>
                            {notification.actor_name !== currentUser?.username ? (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} joined partea {' '}
                                        <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {entityDetail?.entity_name || related_entity_type}
                                        </a>                                        
                                        {' ('}invited by {' '}
                                        <a href={`/user/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>{')'}
                                    </span>
                                </span>
                            ) : (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        You are now a member of partea {' '}
                                        <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {entityDetail?.entity_name}
                                        </a>
                                    </span>
                                </span>
                            )}
                            </>
                        );
                
                    default:
                        return 'requested to collaborate on'
                    }
            case 'teabate_argument_post':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} posted {properties.argumentType || 'an argument'} on {isCoauthor ? 'a teabate you co-authored' : 'your teabate'}{': '}
                            <a href={`/teabate/${notification.related_entity_id}`} className='font-semibold'>
                                {properties?.entityTitle ? `${properties.entityTitle}` : ''}
                            </a>
                        </span>
                        {properties?.commentPreview && (
                            <div className="ml-0 mt-1 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-2 rounded-md max-w-full overflow-hidden text-ellipsis">
                                {properties.commentPreview}
                            </div>
                        )}
                    </span>
                );
            case 'teabate_argument_reply':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} replied to {isCoauthor ? 'an argument you co-authored' : 'your argument'} with a{' '}
                            {properties.argumentType === 'agree' ? 'supporting' : 'counter'} argument
                        </span>
                        {properties?.commentPreview && (
                            <div className="ml-0 mt-1 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-2 rounded-md max-w-full overflow-hidden text-ellipsis">
                                {properties.commentPreview}
                            </div>
                        )}
                    </span>
                );
            case 'teabate_argument_mention':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} mentioned you in a teabate argument
                            {properties?.entityTitle ? ` on "${properties.entityTitle}"` : ''}
                        </span>
                        {properties?.commentPreview && (
                            <div className="ml-0 mt-1 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-2 rounded-md max-w-full overflow-hidden text-ellipsis">
                                {properties.commentPreview}
                            </div>
                        )}
                    </span>
                );
            case 'crosspost_new':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} posted a crosspost from {' '}
                            <a href={`/${notification.related_entity_type}/${notification.related_entity_id}`} className='font-semibold'>
                                {notification.related_entity_type}
                            </a>
                        </span>
                    </span>
                );
            case 'crosspost_mention':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} mentioned you in a crosspost from {' '}
                            <a href={`/${notification.related_entity_type}/${notification.related_entity_id}`} className='font-semibold'>
                                {notification.related_entity_type}
                            </a>
                        </span>
                    </span>
                );
            case 'crosspost_profile':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}  crossposted someone else's content
                            <a href={`/${notification.related_entity_type}/${notification.related_entity_id}`} className='font-semibold'>
                                {notification.related_entity_type}
                            </a>
                        </span>
                    </span>
                );
            case 'new_content':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} posted a new {resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}{': '}
                            <a href={`/${resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || notification.properties.entityTitle) === '[no_title]' ? '' : (notification.entity_name || notification.properties.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'new_action':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} posted a new action
                            <a href={`/${notification.entity_type}/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || notification.properties.entityTitle) === '[no_title]' ? '' : (notification.entity_name || notification.properties.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'new_teabate':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} posted a new teabate
                            <a href={`/teabate/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || notification.properties.entityTitle) === '[no_title]' ? '' : (notification.entity_name || notification.properties.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'subscription_comment':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} commented on {related_entity_type === 'issue' || (related_entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            <a href={`/${resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}/${related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}
                            </a>
                            {` you're subscribed to`}
                        </span>
                        {properties?.commentPreview && (
                            <a href={`/${notification.entity_type}/${notification.entity_id}`} className='text-eoe-text dark:text-eoe-dark-text max-w-full overflow-hidden text-ellipsis'>
                                {' '}{properties.commentPreview}
                            </a>
                        )}
                    </span>
                );
            case 'subscription_impact':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} documented a new impact on {related_entity_type === 'issue' || (related_entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            <a href={`/${resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}/${related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}
                            </a>
                            {` you're subscribed to: `}
                            <a href={`/${resolveEntityDisplayType(notification.entity_type, properties?.is_teabate)}/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || properties?.entityTitle) === '[no_title]' ? '' : (notification.entity_name || properties?.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'subscription_teabate':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} started a teabate on {related_entity_type === 'issue' || (related_entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            <a href={`/${resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}/${related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}
                            </a>
                            {` you're subscribed to: `}
                            <a href={`/teabate/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || properties?.entityTitle) === '[no_title]' ? '' : (notification.entity_name || properties?.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'subscription_solution':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} proposed a new solution to {related_entity_type === 'issue' || (related_entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            <a href={`/${resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}/${related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}
                            </a>
                            {` you're subscribed to: `}
                            <a href={`/${notification.entity_type}/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || properties?.entityTitle) === '[no_title]' ? '' : (notification.entity_name || properties?.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'subscription_action':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} shared a new action for {related_entity_type === 'issue' || (related_entity_type === 'impact' && !properties?.is_teabate) ? 'an ' : 'a '}
                            <a href={`/${resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}/${related_entity_id}`} className='font-medium'>
                                {resolveEntityDisplayType(related_entity_type, properties?.is_teabate)}
                            </a>
                            {` you're subscribed to: `}
                            <a href={`/${notification.entity_type}/${notification.entity_id}`} className='font-semibold'>
                                {(notification.entity_name || properties?.entityTitle) === '[no_title]' ? '' : (notification.entity_name || properties?.entityTitle)}
                            </a>
                        </span>
                    </span>
                );
            case 'subscription_influencer':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {related_entity_type && related_entity_id ? (
                                <>
                                    <a href={`/${related_entity_type}/${related_entity_id}`} className='font-semibold'>
                                        {notification.properties.subscribedIssueName}
                                    </a>
                                </>
                            ) : `An issue you're subscribed to `} {`was added as an influencer of `}
                            {entity_type && entity_id ? (
                                <>
                                    <a href={`/${entity_type}/${encodeURIComponent(notification.properties.topicName)}`} className='font-semibold'>
                                        {notification.properties.topicName}
                                    </a>
                                </>
                            ) : null}
                            {entity_type && entity_id ? (
                                <>
                                    {`: `}
                                    <a href={`/${entity_type}/${encodeURIComponent(notification.properties.compoundIssueName?.replace(/ /g, '_'))}`} className='font-semibold'>
                                        {notification.properties.subjectName} → {notification.properties.topicName}
                                    </a>
                                </>
                            ) : null}
                        </span>
                    </span>
                );
            case 'subscription_influenced':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {related_entity_type && related_entity_id ? (
                                <>
                                    <a href={`/${related_entity_type}/${encodeURIComponent(notification.properties.subscribedIssueName)}`} className='font-semibold'>
                                        {notification.properties.subscribedIssueName}
                                    </a>
                                </>
                            ) : `An issue you're subscribed to `} {`was added as being influenced by `}
                            {entity_type && entity_id ? (
                                <>
                                    <a href={`/${entity_type}/${encodeURIComponent(notification.properties.subjectName)}`} className='font-semibold'>
                                        {notification.properties.subjectName}
                                    </a>
                                </>
                            ) : null}
                            {entity_type && entity_id ? (
                                <>
                                    {`: `}
                                    <a href={`/${entity_type}/${encodeURIComponent(notification.properties.compoundIssueName?.replace(/ /g, '_'))}`} className='font-semibold'>
                                        {notification.properties.subjectName} → {notification.properties.topicName}
                                    </a>
                                </>
                            ) : null}
                        </span>
                    </span>
                );
            case 'content_award_received': {
                const actorLabel = notification.actor_name || notification.actor_team_name || notification.actor_display_name;
                const displayEntityType = resolveEntityDisplayType(notification.entity_type, properties?.is_teabate);
                const entityHref = displayEntityType && notification.entity_id
                    ? `/${displayEntityType}/${notification.entity_id}`
                    : null;
                const rawEntityTitle = `${notification.entity_name || properties?.entityTitle || ""}`.trim();
                const entityTitle = rawEntityTitle && rawEntityTitle !== '[no_title]' ? rawEntityTitle : "";

                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {actorLabel}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}
                            {entityHref && entityTitle ? (
                                <a href={entityHref} className='font-semibold text-eoe-text dark:text-eoe-dark-text'>
                                    {notificationUi.awardReceivedEntity.replace("{entityTitle}", entityTitle)}
                                </a>
                            ) : notificationUi.awardReceivedContent}
                        </span>
                    </span>
                );
            }
            case 'quiz_player_joined':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}{notificationUi.quizPlayerJoined}
                        </span>
                    </span>
                );
            case 'quiz_player_completed':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}
                            {notification.properties?.quiz_phase === "part2"
                                ? notificationUi.quizWaitingPart2
                                : notificationUi.quizWaitingPart1}
                        </span>
                    </span>
                );
            case 'quiz_all_completed':
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}{notificationUi.quizAllCompleted}
                        </span>
                    </span>
                );
            default:
                return (
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '}{notificationUi.unknownNotificationMessage}
                        </span>
                    </span>
                );
        }
    };

    const handleNotificationClick = async (notification: NotificationProps) => {
        if (!notification.is_read) {
            await markNotificationAsRead(notification.id as number);
        }

        if (typeof notification.properties?.conversationPublicId === "string" && notification.properties.conversationPublicId.trim().length > 0) {
            navigate(`/chat/${notification.properties.conversationPublicId}`);
            return;
        }

        if (typeof notification.properties?.quiz_code === "string" && notification.properties.quiz_code.trim().length > 0) {
            navigate(`/quiz/${notification.properties.quiz_code}`);
        }
    };

    const handleRequestAction = async (notification: NotificationProps, accept: boolean) => {
        try {
            await api.patch(apiRoutes.requests.byId(notification.request_id || ""), {
                status: accept ? 'accepted' : 'rejected'
            });
            const newNotification = await api.get(apiRoutes.notifications.byId(notification.id));

            if (accept) {
                // Update notification status for accepted requests
                // Sanitize the incoming notification to guarantee safe actor_name
                const sanitized = sanitizeNotification(newNotification.data as NotificationProps);

                setNotifications(prevNotifications =>
                    prevNotifications.map(notif =>
                        notif.id === notification.id 
                            ? { 
                                ...notif, 
                                ...sanitized
                            } 
                            : notif
                    )
                );
            } else {
                // Remove rejected notifications
                setNotifications(prevNotifications =>
                    prevNotifications.filter(notif => parseInt(notif.id as string, 10) !== parseInt(notification.id as string, 10))
                );
            }

            // Mark notification as read
            await markNotificationAsRead(notification.id as number);
        } catch (error) {
            console.error('Error handling request action:', error);
            // Optionally add error handling UI feedback here
        }
    };

    const groupNotificationsByDate = (notifications: NotificationProps[]) => {
        const today = dayjs();
        return notifications.reduce((groups, notification) => {
            const notifDate = dayjs(notification.updated_at || notification.created_at);
            const key = notifDate.isSame(today, 'day') ? 'today' : 'earlier';
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(notification);
            return groups;
        }, {} as Record<string, NotificationProps[]>);
    };

    const groupedNotifications = groupNotificationsByDate(notifications);

    // Load entity details when notifications change
    useEffect(() => {
        notifications.forEach(notification => {
            loadEntityDetails(notification);
        });
    }, [notifications, loadEntityDetails]);

    // Add a function to handle push notification permission and registration
    const handlePushRegistration = async () => {
        if (!pushSupported) {
            toast.error('Push notifications are not supported in your browser.');
            return;
        }

        try {
            setPushRegistering(true);
            
            // Request permission
            const permission = await requestNotificationPermission();
            
            if (permission !== 'granted') {
                toast.error('Notification permission denied. Please enable notifications in your browser settings.');
                return;
            }
            
            // Register service worker
            const registration = await registerServiceWorker();
            
            if (!registration) {
                toast.error('Failed to register service worker for push notifications.');
                return;
            }
            
            // Subscribe to push notifications
            const subscription = await subscribeToPushNotifications(registration);
            
            if (subscription) {
                // Persist global push preference like settings does
                try {
                    await api.put(apiRoutes.user.notificationPreferences, {
                        notification_type: 'all',
                        active: true,
                        channel: 'push'
                    });
                } catch (prefErr) {
                    console.error('Failed to save push preference:', prefErr);
                    // Non-fatal; subscription exists, but prefer to inform the user
                }
                toast.success('Push notifications enabled successfully!');
                setHasPushSubscription(true);
            } else {
                toast.error('Failed to enable push notifications.');
            }
        } catch (error) {
            console.error('Error setting up push notifications:', error);
            toast.error('An error occurred while setting up push notifications.');
        } finally {
            setPushRegistering(false);
        }
    };

    // Render push subscription toggle when there is no subscription
    const renderPushPrompt = () => {
        if (!pushSupported) return null;
        if (hasPushSubscription) return null;

        return (
            <div className="group flex justify-between items-center bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md mb-4">
                <div className="flex flex-col items-start">
                    <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Web push notifications</span>
                    <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                        Get notified even when you are not on the site.
                    </p>
                </div>
                <input
                    type="checkbox"
                    className="toggle-switch-input col-span-1 flex-shrink-0"
                    checked={false}
                    disabled={pushRegistering}
                    onChange={() => handlePushRegistration()}
                />
            </div>
        );
    };

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
            >
                Close
            </button>
        </div>
    );

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose}
            title={
                <div className="flex justify-between items-center w-full pr-8">
                    <span>Notifications</span>
                </div>
            }
            footer={footer}
            stickyFooter
        >
            <div className="flex flex-col w-full divide-y divide-eoe-border dark:divide-eoe-dark-border max-h-[calc(100vh-8rem)] overflow-y-auto space-y-1 mt-1">
                {renderPushPrompt()}
                
                {loading && notifications.length === 0 ? (
                    <div className="pt-4">
                        <LoadingSkeleton variant="entity-avatar" avatarSize="h-9 w-9" count={10} margin="mx-2" backgroundColor="bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover" className="bg-transparent"/>
                    </div>
                ) : notifications.length === 0 ? (
                    <EmptyState title="All caught up!" subtitle="When someone interacts with your content or profile, you'll see it here." className="pt-12" />
                ) : (
                    <>
                            <>
                                <div className="flex justify-between items-center w-full">
                                    <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
                                        Today
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <button 
                                            className="text-eoe-text dark:text-eoe-dark-text hover:text-eoe-text-sub dark:hover:text-eoe-dark-text-sub"
                                            onClick={handleMarkAllAsRead}
                                        >
                                            Mark all as read
                                        </button>
                                        <button 
                                            className="p-1 rounded-full hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                                            onClick={() => {
                                                navigate('/settings/notifications');
                                                onClose();
                                            }}
                                        >
                                            <Settings className="size-5 stroke-eoe-text dark:stroke-eoe-dark-text" />
                                        </button>
                                    </div>
                                </div>
                                {groupedNotifications.today && groupedNotifications.today.length > 0 && groupedNotifications.today.map((notification, index) => (
                                    <div
                                        key={notification.id}
                                        ref={index === notifications.length - 1 ? lastNotificationElementRef : null}
                                        className={`py-2 flex items-start gap-3 rounded-md 
                                            ${notification.is_read ? 'cursor-normal' : 'cursor-pointer'}
                                            ${notification.is_read ? ' bg-eoe-bg dark:bg-eoe-dark-bg-hover hover:bg-eoe-bg-modal-hover hover:dark:bg-eoe-dark-bg-modal-hover' : ' hover:bg-eoe-bg hover:dark:bg-eoe-dark-bg-hover bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover'}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <>                                                
                                            {getNotificationAvatar(notification)}
                                        </>

                                        <div className={`flex-1 min-w-0 ${notification.notification_type === 'coauthor_invite' ? 'flex flex-col gap-2' : ''}`}>
                                            <div className="flex items-baseline">
                                                {getNotificationMessage(notification)}
                                            </div>
                                            <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                                {formatNotificationTimestamp(notification?.updated_at || notification.created_at)}
                                            </span>
                                            {notification.request_status === 'pending' && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, true);
                                                        }}
                                                        className="btn-primary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, false);
                                                        }}
                                                        className="btn-secondary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <X className="w-4 h-4" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </>
                        
                        {groupedNotifications.earlier?.length > 0 && (
                            <>
                                <div className="py-2 font-medium text-eoe-text dark:text-eoe-dark-text">
                                    Earlier
                                </div>
                                {groupedNotifications.earlier.map((notification, index) => (
                                    <div
                                        key={notification.id}
                                        ref={index === notifications.length - 1 ? lastNotificationElementRef : null}
                                        className={`py-2 flex items-start gap-3 rounded-md 
                                            cursor-pointer
                                            ${notification.is_read ? ' bg-eoe-bg dark:bg-eoe-dark-bg-hover hover:bg-eoe-bg-modal-hover hover:dark:bg-eoe-dark-bg-modal-hover' : ' hover:bg-eoe-bg hover:dark:bg-eoe-dark-bg-hover bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover'}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        
                                        {getNotificationAvatar(notification)}
                                        
                                        <div className={`flex-1 min-w-0`}>
                                            <div className="flex items-baseline">
                                                {getNotificationMessage(notification)}
                                            </div>
                                            <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                                {formatNotificationTimestamp(notification?.updated_at || notification.created_at)}
                                            </span>
                                            {notification.request_status === 'pending' && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, true);
                                                        }}
                                                        className="btn-primary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, false);
                                                        }}
                                                        className="btn-secondary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <X className="w-4 h-4" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}
                {loading && notifications.length > 0 && (
                    <div className="p-4 text-center text-eoe-text dark:text-eoe-dark-text">
                        Loading more...
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default NotificationsModal; 